import { computed } from 'vue'
import type { ApiResponse } from '~/types/api'
import type { LoginData } from '~/types/user'
import type { WeChatAuthOptions } from '~/types/auth'

interface PublicWeChatConfig {
  wechatStateKey?: string
  wechatRedirectUri?: string
  wechatWebAppId?: string
}

export const WECHAT_LOGIN_SUCCESS_KEY = 'wechat_login_success'

let wxLoginScriptPromise: Promise<void> | null = null

export function useWeChatAuth() {
  const config = useRuntimeConfig()
  const publicConfig = config.public as PublicWeChatConfig
  const api = useNuxtApp().$api as <T = any>(
    path: string,
    opts?: { method?: string; body?: any }
  ) => Promise<ApiResponse<T>>

  const stateKey = computed<string>(() => publicConfig.wechatStateKey || 'wechat_login_state')

  const ensureWxLoginScript = async () => {
    if (import.meta.server || typeof window === 'undefined') return
    if (window.WxLogin) return

    if (!wxLoginScriptPromise) {
      wxLoginScriptPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load WxLogin SDK'))
        document.head.appendChild(script)
      }).finally(() => {
        // allow future retries if needed
        setTimeout(() => { wxLoginScriptPromise = null }, 0)
      })
    }

    await wxLoginScriptPromise
  }

  const resolveRedirectUri = (): string => {
    const configured = publicConfig.wechatRedirectUri ?? '/wechat-callback'
    if (/^https?:\/\//i.test(configured)) {
      return configured
    }
    if (typeof window !== 'undefined' && window.location) {
      const url = new URL(configured, window.location.origin)
      return url.toString()
    }
    return configured
  }

  const initWeChatQR = async (containerId: string, options: Partial<WeChatAuthOptions> = {}) => {
    if (import.meta.server || typeof window === 'undefined') {
      throw new Error('WxLogin is only available in client environment')
    }

    const appId = publicConfig.wechatWebAppId
    if (!appId) {
      throw new Error('wechatWebAppId is not configured')
    }

    const state = options.state || generateRandomString(16)
    localStorage.setItem(stateKey.value, state)

    await ensureWxLoginScript()

    const container = document.getElementById(containerId)
    if (container) {
      container.innerHTML = ''
    }

    const WxLoginCtor = window.WxLogin
    if (!WxLoginCtor) {
      throw new Error('WxLogin SDK is not available')
    }

    new WxLoginCtor({
      self_redirect: options.selfRedirect ?? false,
      id: containerId,
      appid: appId,
      scope: 'snsapi_login',
      redirect_uri: encodeURIComponent(resolveRedirectUri()),
      state,
      style: options.style ?? 'black',
      href: options.styleHref ?? '',
      stylelite: options.styleLite ? 1 : undefined
    })

    return state
  }

  const loginWithWeChatCode = async (wxCode: string): Promise<LoginData> => {
    if (!wxCode) {
      throw new Error('wxCode is required')
    }
    const resp = await api('/account/login/wx', {
      method: 'POST',
      body: {
        wx_code: wxCode,
        wx_type: 'web'
      }
    })

    const d: any = (resp as any)?.data ?? resp
    return {
      uid: d.uid,
      token: d.token,
      refreshToken: d.refresh_token,
      userName: d.user_name || `user_${d.uid}`,
      avatarUrl: d.avatar_url || '/images/my-page/avatar_default.png'
    }
  }

  return {
    initWeChatQR,
    loginWithWeChatCode,
    stateKey,
    resolveRedirectUri
  }
}

function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

declare global {
  interface Window {
    WxLogin?: new (options: {
      self_redirect?: boolean
      id: string
      appid: string
      scope: string
      redirect_uri: string
      state: string
      style?: string
      href?: string
      stylelite?: number
    }) => void
  }
}
